import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';

const checkIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--orange)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const starIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="var(--orange)" stroke="none">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

function getDaysRemaining(endDate) {
  if (!endDate) return null;
  const end = new Date(endDate);
  const now = new Date();
  const diff = end - now;
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

export default function PremiumMembership() {
  const { t } = useTranslation();
  const [memberships, setMemberships] = useState([]);
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [memRes, shopRes] = await Promise.all([
        api.getMyMemberships(),
        api.getPremiumShops(),
      ]);
      setMemberships(memRes.data || memRes || []);
      setShops(shopRes.data || shopRes || []);
    } catch (err) {
      console.error('Failed to load premium data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRequest = async (shopId) => {
    try {
      setRequesting((prev) => ({ ...prev, [shopId]: true }));
      await api.requestPremiumMembership(shopId);
      await fetchData();
    } catch (err) {
      console.error('Failed to request membership:', err);
    } finally {
      setRequesting((prev) => ({ ...prev, [shopId]: false }));
    }
  };

  const activeMemberships = Array.isArray(memberships) ? memberships : [];

  const features = [
    'Priority Window',
    'On My Way',
    'Preferred Artist',
    'Handoff Protocol',
    'VIP Support',
  ];

  if (loading) {
    return (
      <div style={{ background: 'var(--bg-base)', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '7rem 1.5rem 5rem' }}>
        <div style={{ color: 'var(--orange)', fontSize: '1.2rem' }}>{t('Loading...')}</div>
      </div>
    );
  }

  return (
    <div style={{ background: 'var(--bg-base)', minHeight: '100vh', padding: '7rem 1.5rem 5rem', color: 'var(--text-primary)' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>

        {/* Page Header */}
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <h1 style={{ fontSize: '2.2rem', fontWeight: 700, color: 'var(--orange)', margin: 0, letterSpacing: 1 }}>
            {t('Premium Membership')}
          </h1>
          <p style={{ color: 'var(--text-muted)', marginTop: 8, fontSize: '1rem' }}>
            {t('Exclusive VIP experience for distinguished clients')}
          </p>
        </div>

        {/* ============ SECTION 1 - MY MEMBERSHIPS ============ */}
        {activeMemberships.length > 0 && (
          <div style={{ marginBottom: 64 }}>
            <h2 style={{ color: 'var(--orange)', fontSize: '1.4rem', fontWeight: 600, marginBottom: 24, borderBottom: '1px solid rgba(212,175,55,0.2)', paddingBottom: 12 }}>
              {t('My Memberships')}
            </h2>

            {activeMemberships.map((membership, idx) => {
              const salon = membership.shop || {};
              const salonName = salon.name || t('Premium Salon');
              const price = membership.price || salon.premiumPrice || salon.monthlyPrice;
              const subStart = membership.subscription_start || membership.createdAt || '';
              const subEnd = membership.subscription_end || '';
              const daysRemaining = getDaysRemaining(subEnd);

              return (
                <div key={membership._id || idx} style={{ marginBottom: 48 }}>

                  {/* Plan Card */}
                  <div style={{
                    background: 'var(--bg-card)',
                    border: '2px solid var(--orange)',
                    borderRadius: 16,
                    padding: 32,
                    marginBottom: 24,
                  }}>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: 2, margin: '0 0 4px' }}>
                      {salonName}
                    </p>
                    <h3 style={{ color: 'var(--orange)', fontSize: '1.6rem', fontWeight: 700, margin: '0 0 8px' }}>
                      {t('VIP Premium')}
                    </h3>
                    {price != null && (
                      <p style={{ color: 'var(--orange)', fontSize: '2rem', fontWeight: 700, margin: '0 0 24px' }}>
                        ${price}<span style={{ fontSize: '0.9rem', fontWeight: 400, color: 'var(--text-muted)' }}> / {t('month')}</span>
                      </p>
                    )}

                    {/* Divider */}
                    <div style={{ height: 1, background: 'var(--border)', marginBottom: 24 }} />

                    {/* Features */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 24 }}>
                      {features.map((feat) => (
                        <div key={feat} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          {checkIcon}
                          <span style={{ color: 'var(--text-primary)', fontSize: '0.95rem' }}>{t(feat)}</span>
                        </div>
                      ))}
                    </div>

                    {/* Subscription Dates */}
                    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center' }}>
                      <div>
                        <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 1 }}>
                          {t('Start')}
                        </span>
                        <p style={{ color: 'var(--text-primary)', fontSize: '0.95rem', margin: '4px 0 0', fontWeight: 600 }}>
                          {subStart ? new Date(subStart).toLocaleDateString() : '--'}
                        </p>
                      </div>
                      <div>
                        <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 1 }}>
                          {t('End')}
                        </span>
                        <p style={{ color: 'var(--text-primary)', fontSize: '0.95rem', margin: '4px 0 0', fontWeight: 600 }}>
                          {subEnd ? new Date(subEnd).toLocaleDateString() : '--'}
                        </p>
                      </div>
                      {daysRemaining != null && (
                        <div>
                          <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 1 }}>
                            {t('Days Remaining')}
                          </span>
                          <p style={{ color: 'var(--orange)', fontSize: '0.95rem', margin: '4px 0 0', fontWeight: 700 }}>
                            {daysRemaining}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Manage Section */}
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                    <button style={{
                      flex: 1, minWidth: 200, padding: '14px 24px', background: 'transparent',
                      border: '1px solid var(--border)', borderRadius: 10, color: 'var(--text-muted)',
                      fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer',
                    }}>
                      {t('Pause Membership')}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ============ SECTION 2 - AVAILABLE PREMIUM SALONS ============ */}
        <div>
          <h2 style={{ color: 'var(--orange)', fontSize: '1.4rem', fontWeight: 600, marginBottom: 24, borderBottom: '1px solid rgba(212,175,55,0.2)', paddingBottom: 12 }}>
            {t('Available Premium Salons')}
          </h2>

          {Array.isArray(shops) && shops.length === 0 && (
            <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 40 }}>
              {t('No premium salons available at the moment.')}
            </p>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 }}>
            {Array.isArray(shops) && shops.map((shop) => {
              const shopId = shop._id || shop.id;
              const membershipStatus = shop.membership_status || null;
              const isActive = membershipStatus === 'active';
              const isPending = membershipStatus === 'pending_approval' || membershipStatus === 'pending';

              return (
                <div key={shopId} style={{
                  background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14,
                  padding: 24, display: 'flex', flexDirection: 'column',
                }}>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ color: 'var(--text-primary)', fontSize: '1.15rem', fontWeight: 600, margin: '0 0 6px' }}>
                      {shop.name}
                    </h3>
                    {shop.category && (
                      <p style={{ color: 'var(--text-muted)', fontSize: '0.82rem', margin: '0 0 10px', textTransform: 'uppercase', letterSpacing: 1 }}>
                        {shop.category}
                      </p>
                    )}
                    {shop.rating != null && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16 }}>
                        {starIcon}
                        <span style={{ color: 'var(--orange)', fontWeight: 600, fontSize: '0.9rem' }}>{shop.rating}</span>
                      </div>
                    )}
                    {(shop.monthly_price || shop.premiumPrice || shop.monthlyPrice) != null && (
                      <p style={{ color: 'var(--orange)', fontSize: '1.5rem', fontWeight: 700, margin: '0 0 20px' }}>
                        ${shop.monthly_price || shop.premiumPrice || shop.monthlyPrice}
                        <span style={{ fontSize: '0.85rem', fontWeight: 400, color: 'var(--text-muted)' }}> / {t('month')}</span>
                      </p>
                    )}
                  </div>

                  {isActive ? (
                    <div style={{
                      padding: '10px 0', textAlign: 'center',
                      borderRadius: 8, color: 'var(--orange)', fontWeight: 600, fontSize: '0.9rem',
                      border: '1px solid var(--orange)',
                    }}>
                      {t('Active')}
                    </div>
                  ) : isPending ? (
                    <div style={{
                      padding: '10px 0', textAlign: 'center',
                      borderRadius: 8, color: 'var(--text-muted)', fontWeight: 600, fontSize: '0.9rem',
                      border: '1px solid var(--border)',
                    }}>
                      {t('Pending')}
                    </div>
                  ) : (
                    <button
                      disabled={requesting[shopId]}
                      onClick={() => handleRequest(shopId)}
                      style={{
                        padding: '12px 0', width: '100%',
                        background: 'var(--orange)',
                        border: 'none', borderRadius: 8, color: '#000',
                        fontWeight: 700, fontSize: '0.95rem',
                        cursor: requesting[shopId] ? 'wait' : 'pointer',
                        opacity: requesting[shopId] ? 0.6 : 1,
                      }}
                    >
                      {requesting[shopId] ? t('Requesting...') : t('Request Membership')}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
